import React from 'react';
import EnumInput from '../../../../../misc/EnumInput';
import CheckboxListItem from '../../../../../misc/CheckboxListItem';
import NullableSelect from '../../../../../misc/NullableSelect';
import * as GoogleUser from '../../../../../../util/GoogleUserExportTypes';
import { OptionItem, OnChangeCallback } from '../../../../../../util/UtilityTypes';
import { update } from '../../../../../../util/Helper';
import { FormGroup } from 'reactstrap';
import { useTranslation } from 'react-i18next';

interface GeneralSettings {
    displayNamePolicy: GoogleUser.DisplayNamePolicy
    includeHomePhone: boolean
    includeMobilePhone: boolean
    includeWorkPhone: boolean
    includeHomeAddress: boolean
    includeUserOrganisations: GoogleUser.UserOrganisationType
}

interface GoogleUserDisplayNamePolicyProps {
    generalSettings: GeneralSettings
    suffixOptions: OptionItem<number>[]
    configurationType: string
    validation: string[]
    disabled?: boolean
    onChange: OnChangeCallback<GeneralSettings | undefined>
}

const GoogleUserGeneralSettings: React.FC<GoogleUserDisplayNamePolicyProps> = (props) => {
    const { t } = useTranslation(['configuration']);

    const updateConfig = (updates: Partial<GeneralSettings>) => {
        update('', props.generalSettings, updates, props.onChange)
    }

    return (<>
        <EnumInput
            title={t('googleUserExport.generalSettings.displayName.title')}
            options={GoogleUser.displayNameFormats.map(x => ({value: x, text: t(`googleUserExport.generalSettings.displayName.formatTypes.${x}`) }) )}
            disabled={props.disabled}
            value={props.generalSettings.displayNamePolicy.format}
            name={`${props.configurationType}-displayNamePolicy.format`}
            onChange={(n, v) => updateConfig({ displayNamePolicy: { ...props.generalSettings.displayNamePolicy, format: v } })}
        />

        <NullableSelect
            options={props.suffixOptions}
            title={t('googleUserExport.generalSettings.displayName.suffix.title')}
            defaultValue={{ value: 0, text:  t('googleUserExport.generalSettings.displayName.suffix.defaultValue')  }}
            disabled={props.disabled}
            checkboxTitle={t('googleUserExport.generalSettings.displayName.suffix.activate')}
            value={props.generalSettings.displayNamePolicy.suffix}
            name={`${props.configurationType}-displayNamePolicy.suffix`}
            onChange={(n, v) => updateConfig({ displayNamePolicy: { ...props.generalSettings.displayNamePolicy, suffix: v } })}
            invalidFeedback={t('googleUserExport.generalSettings.displayName.suffix.missing')}
            invalid={props.validation.indexOf(`${props.configurationType}.displayNamePolicy.suffix`) !== -1}
        />

        <FormGroup>
            <h5>{t('googleUserExport.generalSettings.phoneNumber.title')}</h5>
            <CheckboxListItem
                text={t('googleUserExport.generalSettings.phoneNumber.work')}
                value={props.generalSettings.includeWorkPhone}
                id="includeWorkPhone"
                name="includeWorkPhone"
                onChange={(v) => updateConfig({ includeWorkPhone: v.target.checked })}
                disabled={props.disabled}
            />
            <CheckboxListItem
                text={t('googleUserExport.generalSettings.phoneNumber.home')}
                value={props.generalSettings.includeHomePhone}
                id="includeHomePhone"
                name="includeHomePhone"
                onChange={(v) => updateConfig({ includeHomePhone: v.target.checked })}
                disabled={props.disabled}
            />
            <CheckboxListItem
                text={t('googleUserExport.generalSettings.phoneNumber.mobile')}
                value={props.generalSettings.includeMobilePhone}
                id="includeMobilePhone"
                name="includeMobilePhone"
                onChange={(v) => updateConfig({ includeMobilePhone: v.target.checked })}
                disabled={props.disabled}
            />
        </FormGroup>

        <FormGroup>
            <h5>{t('googleUserExport.generalSettings.address.title')}</h5>
            <CheckboxListItem
                text={t('googleUserExport.generalSettings.address.home')}
                value={props.generalSettings.includeHomeAddress}
                id="includeHomeAddress"
                name="includeHomeAddress"
                onChange={(v) => updateConfig({ includeHomeAddress: v.target.checked })}
                disabled={props.disabled}
            />
        </FormGroup>

        <FormGroup>
            <EnumInput
                title={t('googleUserExport.generalSettings.organisation.title')}
                options={GoogleUser.userOrganisation.map(x => ({ value: x, text: t(`googleUserExport.generalSettings.organisation.userOrganisationOptions.${x}` )}))}
                disabled={props.disabled}
                value={props.generalSettings.includeUserOrganisations}
                name={`${props.configurationType}-includeUserOrganisations`}
                onChange={(n, v) => updateConfig({ includeUserOrganisations: v })}
            />
        </FormGroup>
    </>);
}

export default GoogleUserGeneralSettings;
